import { FC } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  VStack,
  Button,
  HStack,
} from "@chakra-ui/react";
import { Header } from "@/components/Header";
import { Seo } from "@/components/Seo";
import { PublicLayout } from "@/layouts/PublicLayout";
import { Locale } from "@/constants/landingPages";

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQContent {
  meta: {
    title: string;
    description: string;
  };
  hero: {
    title: string;
    subtitle: string;
  };
  faqs: FAQItem[];
  cta: {
    title: string;
    description: string;
    primaryButton: {
      text: string;
      url: string;
    };
    secondaryButton: {
      text: string;
      url: string;
    };
  };
}

interface FAQProps {
  content: FAQContent;
  locale: Locale;
}

export const FAQ: FC<FAQProps> = ({ content, locale }) => {
  const baseUrl = process.env.NEXT_PUBLIC_URL || "https://flexibooking.sk";
  const currentUrl = `${baseUrl}/${locale}/faq`;

  return (
    <PublicLayout>
      <Seo
        title={content.meta.title}
        description={content.meta.description}
        url={currentUrl}
        canonical={currentUrl}
      />

      <Header bg="white" isWithBreadcrumbs={false} locale={locale} />

      <Box as="main" py={{ base: 12, md: 20 }}>
        {/* Hero Section */}
        <Container maxW="container.lg" mb={{ base: 12, md: 16 }}>
          <VStack spacing={4} textAlign="center">
            <Heading
              as="h1"
              size={{ base: "xl", md: "2xl" }}
              fontWeight="bold"
              color="gray.900"
            >
              {content.hero.title}
            </Heading>
            <Text fontSize={{ base: "lg", md: "xl" }} color="gray.600" maxW="2xl">
              {content.hero.subtitle}
            </Text>
          </VStack>
        </Container>

        {/* FAQ Accordion */}
        <Container maxW="container.md" mb={{ base: 12, md: 16 }}>
          <Accordion allowMultiple defaultIndex={[0]}>
            {content.faqs.map((faq) => (
              <AccordionItem
                key={faq.question}
                border="1px solid"
                borderColor="gray.200"
                borderRadius="lg"
                mb={4}
                _last={{ mb: 0 }}
              >
                <h2>
                  <AccordionButton
                    py={5}
                    px={6}
                    _hover={{ bg: "gray.50" }}
                    borderRadius="lg"
                  >
                    <Box
                      flex="1"
                      textAlign="left"
                      fontSize={{ base: "md", md: "lg" }}
                      fontWeight="semibold"
                      color="gray.900"
                    >
                      {faq.question}
                    </Box>
                    <AccordionIcon color="brand.500" fontSize="24px" />
                  </AccordionButton>
                </h2>
                <AccordionPanel
                  pb={6}
                  px={6}
                  fontSize={{ base: "sm", md: "md" }}
                  color="gray.700"
                  lineHeight="1.8"
                >
                  {faq.answer}
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        </Container>

        {/* CTA Section */}
        <Container maxW="container.md">
          <Box
            bg="gray.50"
            borderRadius="2xl"
            p={{ base: 8, md: 12 }}
            textAlign="center"
          >
            <VStack spacing={6}>
              <VStack spacing={3}>
                <Heading as="h2" size="lg" color="gray.900">
                  {content.cta.title}
                </Heading>
                <Text fontSize="md" color="gray.600" maxW="lg">
                  {content.cta.description}
                </Text>
              </VStack>
              <HStack spacing={4} flexWrap="wrap" justifyContent="center">
                <Button
                  as="a"
                  href={content.cta.primaryButton.url}
                  size="lg"
                  colorScheme="brand"
                  px={8}
                >
                  {content.cta.primaryButton.text}
                </Button>
                <Button
                  as="a"
                  href={content.cta.secondaryButton.url}
                  size="lg"
                  variant="outline"
                  colorScheme="brand"
                  px={8}
                >
                  {content.cta.secondaryButton.text}
                </Button>
              </HStack>
            </VStack>
          </Box>
        </Container>
      </Box>
    </PublicLayout>
  );
};
